import { UpperLimitIncomeRequestCode } from '@doda/api-client/aspida/@types';
import {
  JOB_RANKING_LINK_TEXT,
  JOB_SEARCH_LINK_TEXT,
  JOB_TREND_LINK_TEXT,
} from '@doda/common/src/constants/seo/jobContentCommonConstants';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import {
  measureClickJobSearchListLink,
  measureClickJobTrendLink,
  measureClickJobRankingLink,
} from '@doda/common/src/logics/JobContent/Occupation/analytics';

// 計測用パラメータ
export const PAGE_NAME_PC = '職業別の求人数';
export const PAGE_NAME_SP = 'SP_職業別の求人数';
export const CHANNEL = '求人コンテンツ';

// 注釈文言
export const ANNOTATION_TEXT_1 =
  '※このページのデータは最新のdoda掲載の全求人を対象としています。（非公開求人は除く）';
export const ANNOTATION_TEXT_2 =
  '※求人で募集している年収の幅が広い場合、このページ以外の複数の年収帯にもまたがる場合があります。';

// 年収職業パンくず文言
export const OCCUPATION_BREAD_CRUMB_TEXT = '職業・仕事・求人情報';

// Key is occupationL ID
export const OCCUPATION_LARGE_LABEL = {
  '01': '営業職',
  '02': '企画・管理',
  '03': 'SE・インフラエンジニア・Webエンジニア',
  '04': '機械・電気',
  '05': '化学・素材・化粧品 ほか',
  '06': '建設・土木・不動産・プラント・設備',
  '07': 'コンサルタント・士業',
  '08': 'クリエイティブ職',
  '09': '販売・サービス職',
  '10': '公務員・教員 ほか',
  '11': '事務・アシスタント',
  '12': '医療系専門職',
  '13': '金融系専門職',
  '14': '組み込みソフトウェア',
  '15': '食品・香料・飼料',
} as const;

// Cannot use Object.keys(OCCUPATION_LARGE_LABEL) because JavaScript does not guarantee the order of keys during enumeration.
// So we defined keys manually.
export const OCCUPATION_LARGE_KEY = [
  '01',
  '02',
  '03',
  '14',
  '04',
  '05',
  '15',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
] as const;

export type OccupationLargeKey = (typeof OCCUPATION_LARGE_KEY)[number];

// Key is lowerLimitIncome, value is upperLimitIncome
export const MAP_HA_LOWER_TO_UPPER_QUERY_VALUE: Record<
  string,
  UpperLimitIncomeRequestCode | '0'
> = {
  '3': '5', // 年収300万円～400万円
  '5': '7', // 年収400万円～500万円
  '7': '9', // 年収500万円～600万円
  '9': '11', // 年収600万円～700万円
  '11': '12', // 年収700万円～800万円
  '12': '13', // 年収800万円～900万円
  '13': '14', // 年収900万円～1000万円
  '14': '0', // 年収1000万円～
} as const;

export const HA_QUERY_INFO: { [key: string]: HaQueryInfo } = {
  '30': {
    lowerId: '30',
    upperId: '40',
    name: '年収300万円～400万円',
  },
  '40': {
    lowerId: '40',
    upperId: '50',
    name: '年収400万円～500万円',
  },
  '50': {
    lowerId: '50',
    upperId: '60',
    name: '年収500万円～600万円',
  },
  '60': {
    lowerId: '60',
    upperId: '70',
    name: '年収600万円～700万円',
  },
  '70': {
    lowerId: '70',
    upperId: '80',
    name: '年収700万円～800万円',
  },
  '80': {
    lowerId: '80',
    upperId: '90',
    name: '年収800万円～900万円',
  },
  '90': {
    lowerId: '90',
    upperId: '100',
    name: '年収900万円～1000万円',
  },
  '100': {
    lowerId: '100',
    upperId: '0',
    name: '年収1000万円～',
  },
} as const;

export interface HaQueryInfo {
  lowerId: string;
  upperId: string;
  name: string;
}

// ページリード文
export const H1_TITLE = 'の職業・仕事・求人情報';
export const LEAD_SENTENCE =
  'dodaに掲載している職種と業界の中から「%s」の求人情報をご紹介します。';

export const SP_BAR_GRAPH_WIDTH = 100;
export const SP_BAR_GRAPH_HEIGHT = 180;
export const PC_BAR_GRAPH_WIDTH = 120;
export const PC_BAR_GRAPH_HEIGHT = 290;

export const OCCUPATION_PAGE_VISITED_SESSION_STORAGE_KEY =
  'isOccupationPageVisited';

export const OTHER_PAGE_LINK_ITEMS = [
  {
    title: JOB_SEARCH_LINK_TEXT.TITLE,
    description: JOB_SEARCH_LINK_TEXT.DESCRIPTION,
    siteUrl: DODA_SITE_URL.jobSearchList,
    imagePath: JOB_SEARCH_LINK_TEXT.IMAGE_PATH,
    preBtn: true,
    handleMeasurement: measureClickJobSearchListLink,
  },
  {
    title: JOB_TREND_LINK_TEXT.TITLE,
    description: JOB_TREND_LINK_TEXT.DESCRIPTION,
    siteUrl: DODA_SITE_URL.jobTrend,
    imagePath: JOB_TREND_LINK_TEXT.IMAGE_PATH,
    preBtn: false,
    handleMeasurement: measureClickJobTrendLink,
  },
  {
    title: JOB_RANKING_LINK_TEXT.TITLE,
    description: JOB_RANKING_LINK_TEXT.DESCRIPTION,
    siteUrl: DODA_SITE_URL.jobRanking,
    imagePath: JOB_RANKING_LINK_TEXT.IMAGE_PATH,
    preBtn: false,
    handleMeasurement: measureClickJobRankingLink,
  },
];

// Other salary and occupation pages
export const OTHER_OCCUPATION_TITLE = 'ほかの年収帯の職業・仕事を見る';

// 年収
export const OTHER_HA_SEARCH_ITEMS = [
  {
    id: '30',
    seoText: '300万円～',
  },
  {
    id: '40',
    seoText: '400万円～',
  },
  {
    id: '50',
    seoText: '500万円～',
  },
  {
    id: '60',
    seoText: '600万円～',
  },
  {
    id: '70',
    seoText: '700万円～',
  },
  {
    id: '80',
    seoText: '800万円～',
  },
  {
    id: '90',
    seoText: '900万円～',
  },
  {
    id: '100',
    seoText: '1000万円～',
  },
] as const;

// 年収職業ページ_キャリーID
export const OCCUPATION_CARRY_ID = '5100005453';

// 年収職業ページ
export const MEMBER_REGIST = '_memberRegist_';
export const LOGIN = '_login_';
export const INTRO = '_intro_';
export const TOP = 'top';
export const BOTTOM = 'bottom';
export const OCCUPATION = 'Occupation';
export const ANALYTICS_OC_NAME = 'oc__';
